import { useCallback } from 'react';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type MessageActionPayload } from '@atlassian/conversation-assistant-service-api/types/message-actions';

export const useMessageActionedAnalytics = (): {
	fireUiActionedAnalytics: <_TActionCallbackReturnType = unknown>(
		payload: MessageActionPayload,
	) => void;
	fireAIMAUActionedAnalytics: <_TActionCallbackReturnType = unknown>(
		payload: MessageActionPayload,
		agentMessageId: string,
		interactionModeExecuted?: string,
		aiFeatureName?: string,
	) => void;
} => {
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();

	const fireUiActionedAnalytics = useCallback(
		<_TActionCallbackReturnType = unknown,>(payload: MessageActionPayload): void => {
			sendAnalyticsEvent({
				eventType: 'ui',
				action: 'actioned',
				actionSubject: 'rovoChatMessageAction',
				attributes: {
					recipientAgentNamedId: payload.context.recipient_agent_named_id,
					actionKey: payload.context.key,
					resolutionType: payload.context.resolution.type,
				},
			});
		},
		[sendAnalyticsEvent],
	);

	const fireAIMAUActionedAnalytics = useCallback(
		<_TActionCallbackReturnType = unknown,>(
			payload: MessageActionPayload,
			agentMessageId: string,
			interactionModeExecuted?: string,
			aiFeatureName?: string,
		): void => {
			const attributes = {
				actionKey: payload.context.key,
				...(interactionModeExecuted ? { interactionModeExecuted } : {}),
				...(aiFeatureName ? { aiFeatureName } : {}),
			};

			switch (payload.context.resolution.type) {
				case 'actioned':
					trackAIMAUEvent({
						type: 'actioned',
						actionResult: 'messageActionedExternally',
						attributes,
						proactive: false,
						singleInstrumentationID: agentMessageId,
					});
					break;

				case 'confirm':
					trackAIMAUEvent({
						type: 'actioned',
						actionResult: 'confirmActionClicked',
						attributes,
						proactive: false,
						singleInstrumentationID: agentMessageId,
					});
					break;

				case 'cancel':
					trackAIMAUEvent({
						type: 'actioned',
						actionResult: 'cancelActionClicked',
						attributes,
						proactive: false,
						singleInstrumentationID: agentMessageId,
					});
					break;
			}
		},
		[trackAIMAUEvent],
	);

	return {
		fireUiActionedAnalytics,
		fireAIMAUActionedAnalytics,
	};
};
